import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {DataGridActionTypes, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {ShippingFeesRegionsListDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import Button from "../../../../../../components/base/button/button";
import {Add} from "@mui/icons-material";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import FreightRegionsSearchSection from "../../../../../../components/search-sections/freight-regions";
import UpsertFreightRegionDialog from "../../../../../../components/dialogs/upsert-freight-region-dialog";
import Api from "../../../../../../../core/services/api/api";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const initUpsertDialogState = {open: false, data: null};

const exportingChunkSize = 50;

const dataGridSaveConfig = {
    name: 'SystemRegionsListViewDataGrid',
    version: 1,
}

/**
 * Freight regions list action names
 */
const ShippingFeesFreightRegionsActionNames = {
    Edit: 'edit',
    Delete: 'delete'
}

const SystemRegionsListView = () => {
    const [data, _setData] = useState([]);
    const [upsertDialogState, setUpsertDialogState] = useState(initUpsertDialogState);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridControllerRef = useRef();
    const {configureNotebookTags} = useConfigureNotebook('regions-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.regions);
        configureBehavioralTool((tags) => tags.regions);
    }, [configureBehavioralTool, configureNotebookTags]);

    const setData = useCallback((stateOrCallback) => {
        _setData(prevState => {
            let nextState;
            if (typeof stateOrCallback === 'function') {
                nextState = stateOrCallback(prevState);
            } else {
                nextState = stateOrCallback;
            }
            return nextState.map(e => ({
                ...e,
                countries: e.countries?.map(e => ({
                    ...e,
                    name: e.title ?? e.name,
                })),
                provinces: e.provinces?.map(e => ({
                    ...e,
                    name: e.title ?? e.name,
                })),
            }))
        })
    }, [])

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: DataGridExoticColumnFields.selection,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    width: 100,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    sortable: false,
                },
                {
                    title: "Title",
                    name: ShippingFeesRegionsListDataGridColumnNames.title,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Countries",
                    name: ShippingFeesRegionsListDataGridColumnNames.countries,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        size: 300,
                        minWidth: 300,
                    },
                },
                {
                    title: "Provinces/States",
                    name: ShippingFeesRegionsListDataGridColumnNames.provinces,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        size: 300,
                        minWidth: 300,
                    },
                },
                {
                    title: "Postal/Zip Code Formats",
                    name: ShippingFeesRegionsListDataGridColumnNames.postalCodes,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        size: 300,
                        minWidth: 300,
                    },
                },
            ],
        [])

    const [dataGridColumns, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    /**
     * Gets the list of freight regions by calling the api regarding this action
     * If the function is being called for export, the pagination info is passed as the input
     * @param cancelTokenSource
     * @param forExport
     * @param _paginationInfo
     * @return {Promise<ApiResponseDs<*> | void>}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            pagination: args.paginationInfo,
            countryId: args.filters?.country ? JSON.parse(args.filters?.country).id : undefined,
            provinceId: args.filters?.province ? JSON.parse(args.filters?.province).id : undefined,
            postalCode: args.filters?.postalCode,
            title: args.filters?.title,
        };
        const response = await Api.getSystemRegions(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.length ?? 0,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, setData])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Exports the list of freight regions in the system by calling the search function with the pagination info
     * Calls the function for formatting the data
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const canExportRegionsList = operatorAccessLevels.packages.base.exportRegionsList;
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        if (!entries.length)
            return;
        return {
            title: 'Freight Regions List',
            body: entries.map((e, i) => ({
                '': i + 1,
                'Title': e.title ?? '',
                'Countries': e.countries?.map(e => e.name)?.join(', ') ?? '',
                'Provinces/States': e.provinces?.map(e => e.name)?.join(', '),
                'Postal/Zip Codes': e.postalCodes?.join(', '),
            }))
        }
    }, [fetchEntries, isMounted, searchData])

    /**
     * Opens the dialog for adding a new freight region to the system
     */
    const onAddNewRegionClicked = () => {
        setUpsertDialogState({open: true, data: null});
    }

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(company => ({
            key: company.id,
            data: company,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [ShippingFeesRegionsListDataGridColumnNames.title]: company.title,
                [ShippingFeesRegionsListDataGridColumnNames.countries]: company.countries?.map(e => e.name)?.join(', '),
                [ShippingFeesRegionsListDataGridColumnNames.provinces]: company.provinces?.map(e => e.name)?.join(', '),
                [ShippingFeesRegionsListDataGridColumnNames.postalCodes]: company.postalCodes?.join(', '),
            }
        }))
    }, [data])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: ShippingFeesFreightRegionsActionNames.Edit,
                title: 'Edit',
                visibilityConstraints: {
                    minSelectedRows: 1,
                    maxSelectedRows: 1,
                },
            },
            {
                name: ShippingFeesFreightRegionsActionNames.Delete,
                title: 'Delete',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    /**
     * Opens dialog for editing the company information.
     * @param region
     */
    const editSelectedFreightRegion = (region) => {
        setUpsertDialogState({open: true, data: region});
    }

    /**
     * Opens the confirmation dialog regarding this action
     * If the process was confirmed, calls the remove
     */
    const getRemoveConfirmation = (itemsToBeRemoved, allRecordsSelected) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {`Are you sure that you want to remove the selected freight region${itemsToBeRemoved?.length > 1 || allRecordsSelected ? 's' : ''}?`}
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeFreightRegions(itemsToBeRemoved),
        );
    }

    /**
     * Calls the api regarding removing the list of regions form the list
     * @param regionIds list of ids associated to the regions to be removed
     * @private
     */
    const removeFreightRegions = async (regionIds) => {
        searchData.setLoading(true);
        const response = await Api.deleteSystemRegions({ids: regionIds});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.filter(e => !regionIds.includes(e.id)))
            searchData.setPaginationInfo(prevState => ({
                ...prevState,
                length: prevState.length - regionIds.length
            }))
        }
        dataGridControllerRef.current?.resetSelection();
        searchData.setLoading(false);
    }

    /**
     * Adds or updates the current list of freight regions based on the provided (upserted) region.
     * @param region
     */
    const onRegionUpserted = (region) => {
        setData(prevState => {
            const foundIndex = prevState.findIndex(e => e.id === region.id);
            if (foundIndex === -1) {
                return [region, ...prevState];
            }
            return prevState.map(e => e.id === region.id ? {...e, ...region} : e)
        })
        dataGridControllerRef.current?.resetSelection();
    }

    /**
     * Fetches all the regions given the available length of the regions.
     * @param pageLength
     * @return {Promise<*[]>}
     */
    const getAllRegions = async (pageLength) => {
        const _paginationInfo = {
            length: pageLength,
            pageSize: 20,
            currentPage: 1,
        };
        let entries = [];
        while (pageLength > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                continue;
            }
            if (!res?.data?.length) {
                break;
            }
            entries.push(...res?.data ?? []);
            if (!isMounted())
                return entries;
            _paginationInfo.currentPage++;
        }
        return entries;
    }

    /**
     * Handles the action over the selected row in the table based on the name of the action
     * @param action
     * @param selectedRows
     * @param excludedRows
     * @param allRowsSelected
     * @param pagination
     * @return {Promise<void>}
     */
    const onActionInvoked = async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case ShippingFeesFreightRegionsActionNames.Edit: {
                if (allRowsSelected && data.length === 1)
                    editSelectedFreightRegion(data[0]);
                else
                    editSelectedFreightRegion(selectedRows[0].data);
                break;
            }
            case ShippingFeesFreightRegionsActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return getRemoveConfirmation(selectedRows.map(e => e.data.id));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return getRemoveConfirmation(data.map(e => e.id));
                }
                getRemoveConfirmation((await getAllRegions(pagination.length)).map(e => e.id));
                break;
            }
            default:
                break;
        }
    }

    return (
        <>
            <div className={'freight-regions-list-view'}>
                <div className={'page-title-section'}>
                    <div/>
                    <Button
                        className={'d-flex align-items-center justify-content-center'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={onAddNewRegionClicked}
                    >
                        <Add className='icon small mr-2'/>
                        <p className={'button-label'}>Add a new Region</p>
                    </Button>
                </div>
                <FreightRegionsSearchSection searchData={searchData}/>
                <div className={'mb-5'}/>
                <PartnerDataGrid
                    ref={dataGridControllerRef}
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    footerActions={footerActions}
                    // hidePagination
                    // hidePageSize
                    minRowHeight={69}
                    hideFooterActions={data?.length < 1}
                    onActionInvoked={onActionInvoked}
                    state={{
                        sortBy: searchData.orderBy,
                        loading: {state: searchData.loading},
                        density: dataGridDensity,
                        pagination: searchData.paginationInfo,
                    }}
                    getExportInfo={canExportRegionsList ? exportToCsv : undefined}
                    onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                    onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                    onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                />
            </div>
            <UpsertFreightRegionDialog
                close={() => setUpsertDialogState(initUpsertDialogState)}
                open={upsertDialogState.open}
                data={upsertDialogState.data}
                onUpsert={onRegionUpserted}
            />
        </>
    )
}

export default SystemRegionsListView;
